import React, { useState, useEffect, useCallback } from "react";
import PageContainer from "./PageContainer";
import "../assets/transfer-portal.css";
import SportsBasketballIcon from "@mui/icons-material/SportsBasketball";
import SchoolIcon from "@mui/icons-material/School";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import SportsScoreIcon from "@mui/icons-material/SportsScore";
import TimelineIcon from "@mui/icons-material/Timeline";
import UpdateIcon from "@mui/icons-material/Update";
import ClassIcon from "@mui/icons-material/Class";
import SortIcon from "@mui/icons-material/Sort";
import HeightIcon from "@mui/icons-material/Height";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EmailIcon from "@mui/icons-material/Email";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import PersonIcon from "@mui/icons-material/Person";
import LaunchIcon from "@mui/icons-material/Launch";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SchoolTwoToneIcon from "@mui/icons-material/SchoolTwoTone";
import SortByAlphaIcon from "@mui/icons-material/SortByAlpha";
import PercentIcon from "@mui/icons-material/Percent";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import { Link } from "react-router-dom";
import {
  X_factor_getCollegeColor,
  hasLoggedEnoughMinutes,
  DEFAULT_XFACTOR_LABEL,
} from "../components/Utils/functions";
import axios from "axios";
import Loader from "../components/Utils/Loader";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";
import PortalFilterPop from "../components/PortalFilterPop";
import PortalPlayerModal from "../components/PortalPlayerModal";

// Add a helper function to check for duplicates by ID
const removeDuplicates = (existingPlayers, newPlayers) => {
  const existingIds = new Set(existingPlayers.map((player) => player.id));
  return newPlayers.filter((player) => !existingIds.has(player.id));
};

const Portal = () => {
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    position: "all",
    status: "all",
    gradTransfer: "all",
    hasContact: "all",
    yearsRemaining: "all",
    teams: "all",
  });
  const [sortOption, setSortOption] = useState("xfactor");
  const [lastUpdated, setLastUpdated] = useState(
    new Date().toLocaleDateString()
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [showFilterTip, setShowFilterTip] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const [totals, setTotals] = useState({
    committed: 0,
    available: 0,
  });
  const [openFilter, setOpenFilter] = useState(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);

  const positions = ["PG", "SG", "SF", "PF", "C"];

  const filterOptions = {
    position: [
      { value: "all", label: "All Positions" },
      ...positions.map((pos) => ({ value: pos, label: pos })),
    ],
    status: [
      { value: "all", label: "All Status" },
      { value: "committed", label: "Committed" },
      { value: "available", label: "Available" },
    ],
    gradTransfer: [
      { value: "all", label: "All" },
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
    hasContact: [
      { value: "all", label: "All" },
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
    yearsRemaining: [
      { value: "all", label: "All" },
      { value: "1", label: "1 Year" },
      { value: "2", label: "2 Years" },
      { value: "3", label: "3 Years" },
      { value: "4", label: "4 Years" },
    ],
    teams: [
      { value: "all", label: "All Teams" },
      // Teams options will be dynamically populated by the autocomplete
    ],
    sortOption: [
      // { value: "updated", label: "Recently Updated" },
      { value: "xfactor", label: "X-Factor" },
      { value: "ppg", label: "Points (PPG)" },
      { value: "rpg", label: "Rebounds (RPG)" },
      { value: "apg", label: "Assists (APG)" },
      { value: "height", label: "Height" },
      { value: "name", label: "Name" },
    ],
  };

  const handleFilterClick = (filterType) => {
    setOpenFilter(filterType);
  };

  const handleFilterClose = () => {
    setOpenFilter(null);
  };

  const handleFilterSave = (filterType, selectedValue) => {
    if (filterType === "sortOption") {
      setSortOption(selectedValue);
    } else {
      setFilters((prev) => ({
        ...prev,
        [filterType]: selectedValue,
      }));
    }
  };

  // Remove debounce effect and replace with key press handler
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      setDebouncedSearchTerm(searchTerm);
    }
  };

  // Function to fetch players from API
  const fetchPlayers = async (page = 1, limit = 50, shouldAppend = false) => {
    try {
      setLoadingMore(shouldAppend);
      if (!shouldAppend) setLoading(true);

      // Build query parameters
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);

      if (debouncedSearchTerm) {
        params.append("search", debouncedSearchTerm);
      }

      if (filters.position !== "all") {
        // For multiple positions, we pass them as a comma-separated list
        params.append("position", filters.position);
      }

      if (filters.status !== "all") {
        params.append("status", filters.status);
      }

      if (filters.gradTransfer !== "all") {
        params.append(
          "grad_transfer",
          filters.gradTransfer === "yes" ? "true" : "false"
        );
      }

      if (filters.hasContact !== "all") {
        params.append(
          "has_contact",
          filters.hasContact === "yes" ? "true" : "false"
        );
      }

      if (filters.yearsRemaining !== "all") {
        params.append("eligibility", filters.yearsRemaining);
      }

      if (filters.teams !== "all") {
        params.append("teams", filters.teams);
      }

      if (sortOption !== "updated") {
        params.append("sort", sortOption);
      }

      const response = await axios.get(`/api/portal?${params.toString()}`, {
        withCredentials: true,
      });

      const formattedData = response.data.players;
      setLastUpdated(new Date().toLocaleDateString());

      if (shouldAppend) {
        // Filter out any duplicate players before appending
        const uniqueNewPlayers = removeDuplicates(players, formattedData);

        // Only append if there are unique new players
        if (uniqueNewPlayers.length > 0) {
          setPlayers((prevPlayers) => [...prevPlayers, ...uniqueNewPlayers]);
          setFilteredPlayers((prevPlayers) => [
            ...prevPlayers,
            ...uniqueNewPlayers,
          ]);
        }
      } else {
        // Replace existing players
        setPlayers(formattedData);
        setFilteredPlayers(formattedData);
      }

      setPagination(response.data.pagination);

      // Set the totals from the response
      if (response.data.totals) {
        setTotals(response.data.totals);
      }
    } catch (err) {
      console.error("Error fetching players:", err);
      setError("Failed to load player data. Please try again later.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchPlayers(1, 50, false);
  }, []);

  // Fetch new data when filters or sort options change
  useEffect(() => {
    fetchPlayers(1, 50, false);
  }, [filters, sortOption, debouncedSearchTerm]);

  // Update isFiltered state whenever filters change
  useEffect(() => {
    const hasActiveFilters =
      filters.position !== "all" ||
      filters.status !== "all" ||
      filters.gradTransfer !== "all" ||
      filters.hasContact !== "all" ||
      filters.yearsRemaining !== "all" ||
      filters.teams !== "all" ||
      sortOption !== "updated" ||
      debouncedSearchTerm !== "";

    setIsFiltered(hasActiveFilters);
  }, [filters, sortOption, debouncedSearchTerm]);

  const loadMorePlayers = () => {
    if (pagination.page < pagination.pages) {
      fetchPlayers(pagination.page + 1, pagination.limit, true);
      setShowFilterTip(true);

      // Hide filter tip after 5 seconds
      setTimeout(() => {
        setShowFilterTip(false);
      }, 5000);
    }
  };

  const handleCardClick = (playerId, event) => {
    // Prevent the click event from propagating to parent elements
    event.stopPropagation();
    setSelectedPlayerId(playerId);
  };

  const handleCloseModal = () => {
    setSelectedPlayerId(null);
  };

  if (loading && !loadingMore) {
    return (
      <PageContainer>
        <div className="transfer-portal-loading">
          <Loader />
          <p>Loading player data...</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="transfer-portal-container">
        <div className="transfer-portal-header">
          <div className="transfer-portal-title">
            <h1>Transfer Portal</h1>
          </div>

          <div className="transfer-portal-last-updated">
            <UpdateIcon />
            <span>Last Updated: {lastUpdated}</span>
          </div>

          <div className="transfer-portal-search">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search players, schools..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleSearchKeyPress}
            />
          </div>
        </div>

        <div className="transfer-portal-filters">
          <div className="transfer-portal-mobile-scroll-indicator">
            <span>Swipe</span>
            <ArrowForwardIcon fontSize="small" />
          </div>
          <div
            className="transfer-portal-filter-group clickable-filter"
            onClick={() => handleFilterClick("position")}
          >
            <label>
              <FilterListIcon />
              <span>Position</span>
            </label>
            <div className="filter-value">
              {filters.position === "all"
                ? "All Positions"
                : filters.position.includes(",")
                ? "Multiple"
                : filters.position}
            </div>
          </div>

          <div
            className="transfer-portal-filter-group clickable-filter"
            onClick={() => handleFilterClick("teams")}
          >
            <label>
              <SchoolIcon />
              <span>Teams</span>
            </label>
            <div className="filter-value">
              {filters.teams === "all"
                ? "All Teams"
                : filters.teams.includes(",")
                ? "Multiple"
                : filters.teams}
            </div>
          </div>

          <div
            className="transfer-portal-filter-group clickable-filter"
            onClick={() => handleFilterClick("status")}
          >
            <label>
              <SportsScoreIcon />
              <span>Status</span>
            </label>
            <div className="filter-value">
              {filters.status === "all"
                ? "All Status"
                : filters.status.charAt(0).toUpperCase() +
                  filters.status.slice(1)}
            </div>
          </div>

          <div
            className="transfer-portal-filter-group clickable-filter"
            onClick={() => handleFilterClick("gradTransfer")}
          >
            <label>
              <SchoolTwoToneIcon />
              <span>Grad Transfer</span>
            </label>
            <div className="filter-value">
              {filters.gradTransfer === "all"
                ? "All"
                : filters.gradTransfer === "yes"
                ? "Yes"
                : "No"}
            </div>
          </div>

          <div
            className="transfer-portal-filter-group clickable-filter"
            onClick={() => handleFilterClick("hasContact")}
          >
            <label>
              <ContactPhoneIcon />
              <span>Has Contact</span>
            </label>
            <div className="filter-value">
              {filters.hasContact === "all"
                ? "All"
                : filters.hasContact === "yes"
                ? "Yes"
                : "No"}
            </div>
          </div>

          <div
            className="transfer-portal-filter-group clickable-filter"
            onClick={() => handleFilterClick("yearsRemaining")}
          >
            <label>
              <AccessTimeIcon />
              <span>Years Remaining</span>
            </label>
            <div className="filter-value">
              {filters.yearsRemaining === "all"
                ? "All"
                : `${filters.yearsRemaining} Year${
                    filters.yearsRemaining !== "1" ? "s" : ""
                  }`}
            </div>
          </div>

          <div
            className="transfer-portal-filter-group clickable-filter"
            onClick={() => handleFilterClick("sortOption")}
          >
            <label>
              <SortIcon />
              <span>Sort By</span>
            </label>
            <div className="filter-value">
              {sortOption === "updated"
                ? "Recently Updated"
                : sortOption === "xfactor"
                ? "X-Factor"
                : sortOption === "ppg"
                ? "Points (PPG)"
                : sortOption === "rpg"
                ? "Rebounds (RPG)"
                : sortOption === "apg"
                ? "Assists (APG)"
                : sortOption === "height"
                ? "Height"
                : "Name"}
            </div>
          </div>
        </div>

        <PortalFilterPop
          open={!!openFilter}
          filterType={openFilter}
          onClose={handleFilterClose}
          onSave={handleFilterSave}
          selectedValue={
            openFilter === "sortOption"
              ? sortOption
              : openFilter
              ? filters[openFilter].toString()
              : ""
          }
          options={openFilter ? filterOptions[openFilter] : []}
          title={
            openFilter === "position"
              ? "Filter by Position"
              : openFilter === "status"
              ? "Filter by Status"
              : openFilter === "gradTransfer"
              ? "Filter by Grad Transfer"
              : openFilter === "hasContact"
              ? "Filter by Contact"
              : openFilter === "yearsRemaining"
              ? "Filter by Years Remaining"
              : openFilter === "sortOption"
              ? "Sort By"
              : ""
          }
        />

        <div className="transfer-portal-stats">
          <div className="transfer-portal-stat">
            <span className="transfer-portal-stat-number">
              {pagination.total || 0}
            </span>
            <span className="transfer-portal-stat-label">Total Players</span>
          </div>
          <div className="transfer-portal-stat">
            <span className="transfer-portal-stat-number">
              {totals.committed}
            </span>
            <span className="transfer-portal-stat-label">Committed</span>
          </div>
          <div className="transfer-portal-stat">
            <span className="transfer-portal-stat-number">
              {totals.available}
            </span>
            <span className="transfer-portal-stat-label">Available</span>
          </div>
        </div>

        {error && (
          <div className="transfer-portal-error">
            <p>{error}</p>
            <button onClick={() => fetchPlayers(1, 50, false)}>Retry</button>
          </div>
        )}

        <div className="transfer-portal-content">
          {filteredPlayers.length === 0 ? (
            <div className="no-results-box">
              <TimelineIcon className="empty-icon" />
              <h3>Widen your criteria</h3>
              <p>We have no results for the filters selected below.</p>
              <div className="active-filters">
                {debouncedSearchTerm && (
                  <div className="active-filter">
                    <SearchIcon fontSize="small" />
                    <span>Search: {debouncedSearchTerm}</span>
                  </div>
                )}
                {filters.position !== "all" && (
                  <div className="active-filter">
                    <FilterListIcon fontSize="small" />
                    <span>Position: {filters.position}</span>
                  </div>
                )}
                {filters.status !== "all" && (
                  <div className="active-filter">
                    <SportsScoreIcon fontSize="small" />
                    <span>Status: {filters.status}</span>
                  </div>
                )}
                {filters.gradTransfer !== "all" && (
                  <div className="active-filter">
                    <SchoolTwoToneIcon fontSize="small" />
                    <span>
                      Grad Transfer:{" "}
                      {filters.gradTransfer === "yes" ? "Yes" : "No"}
                    </span>
                  </div>
                )}
                {filters.hasContact !== "all" && (
                  <div className="active-filter">
                    <ContactPhoneIcon fontSize="small" />
                    <span>
                      Has Contact: {filters.hasContact === "yes" ? "Yes" : "No"}
                    </span>
                  </div>
                )}
                {filters.yearsRemaining !== "all" && (
                  <div className="active-filter">
                    <AccessTimeIcon fontSize="small" />
                    <span>Years Remaining: {filters.yearsRemaining}</span>
                  </div>
                )}
                {filters.teams !== "all" && (
                  <div className="active-filter">
                    <SchoolIcon fontSize="small" />
                    <span>Team: {filters.teams}</span>
                  </div>
                )}
                {sortOption !== "xfactor" && (
                  <div className="active-filter">
                    <SortIcon fontSize="small" />
                    <span>
                      Sorted by:{" "}
                      {
                        filterOptions.sortOption.find(
                          (opt) => opt.value === sortOption
                        )?.label
                      }
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setDebouncedSearchTerm("");
                  setFilters({
                    position: "all",
                    status: "all",
                    gradTransfer: "all",
                    hasContact: "all",
                    yearsRemaining: "all",
                    teams: "all",
                  });
                  setSortOption("xfactor");
                  fetchPlayers(1, 50, false);
                }}
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <>
              <div className="transfer-portal-grid">
                {filteredPlayers.map((player, index) => {
                  const notEnoughMinutes = !hasLoggedEnoughMinutes(
                    player.games,
                    player.minutes_per_game
                  );
                  return (
                    <div
                      key={player.id || index}
                      className="transfer-portal-card"
                      onClick={(e) => handleCardClick(player.id, e)}
                      style={{ "--animation-order": index % 50 }}
                    >
                      <div className="transfer-portal-card-header">
                        <div className="transfer-portal-player-info">
                          {player.image && (
                            <div className="transfer-portal-player-image">
                              <img
                                src={player.image}
                                alt={player.name}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  // Use one of the backup images randomly instead of placeholder
                                  const backupImages = [
                                    "https://d3mvy6uwazuxyu.cloudfront.net/back-b-2.png",
                                    "https://d3mvy6uwazuxyu.cloudfront.net/back-b-3.png",
                                    "https://d3mvy6uwazuxyu.cloudfront.net/back-b-4.png",
                                  ];
                                  const randomBackup =
                                    backupImages[
                                      Math.floor(
                                        Math.random() * backupImages.length
                                      )
                                    ];
                                  e.target.src = randomBackup;
                                }}
                              />
                            </div>
                          )}
                          <div className="transfer-portal-player-name">
                            <h3>{player.name}</h3>
                            <div className="transfer-portal-player-details-row">
                              <span className="transfer-portal-position">
                                {player.position}
                              </span>
                              <span className="transfer-portal-class">
                                <ClassIcon fontSize="small" />
                                {player.class}
                              </span>
                            </div>
                            {notEnoughMinutes && (
                              <div className="transfer-portal-minutes-note">
                                * Didn't play much:{" "}
                                {Math.round(
                                  player.games * player.minutes_per_game
                                )}{" "}
                                total mins logged
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="transfer-portal-xfactor">
                          <span>X-Factor</span>
                          <div
                            className="transfer-portal-xfactor-value"
                            style={{
                              color: notEnoughMinutes
                                ? "#666"
                                : X_factor_getCollegeColor(player.xfactor),
                            }}
                          >
                            {notEnoughMinutes
                              ? DEFAULT_XFACTOR_LABEL
                              : player.xfactor}
                          </div>
                        </div>
                      </div>

                      <div className="transfer-portal-card-body">
                        <div className="transfer-portal-schools">
                          <div className="transfer-portal-school from">
                            <SchoolIcon />
                            <span>{player.school}</span>
                          </div>
                          <ArrowForwardIcon className="transfer-portal-arrow" />
                          <div className="transfer-portal-school to">
                            {player.commitment ? (
                              <>
                                <SchoolIcon />
                                <span>{player.commitment}</span>
                              </>
                            ) : (
                              <span className="transfer-portal-undecided">
                                Undecided
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="transfer-portal-stats-row">
                          <div
                            className={`transfer-portal-stat-item ${
                              sortOption === "ppg" ? "sort-highlight" : ""
                            }`}
                          >
                            <span className="transfer-portal-stat-value">
                              {player.ppg.toFixed(1)}
                              {sortOption === "ppg" && (
                                <span className="sort-highlight-rank">
                                  #{index + 1}
                                </span>
                              )}
                            </span>
                            <span className="transfer-portal-stat-label">
                              PPG
                            </span>
                          </div>
                          <div
                            className={`transfer-portal-stat-item ${
                              sortOption === "rpg" ? "sort-highlight" : ""
                            }`}
                          >
                            <span className="transfer-portal-stat-value">
                              {player.rpg.toFixed(1)}
                              {sortOption === "rpg" && (
                                <span className="sort-highlight-rank">
                                  #{index + 1}
                                </span>
                              )}
                            </span>
                            <span className="transfer-portal-stat-label">
                              RPG
                            </span>
                          </div>
                          <div
                            className={`transfer-portal-stat-item ${
                              sortOption === "apg" ? "sort-highlight" : ""
                            }`}
                          >
                            <span className="transfer-portal-stat-value">
                              {player.apg.toFixed(1)}
                              {sortOption === "apg" && (
                                <span className="sort-highlight-rank">
                                  #{index + 1}
                                </span>
                              )}
                            </span>
                            <span className="transfer-portal-stat-label">
                              APG
                            </span>
                          </div>
                          <div className="transfer-portal-stat-item">
                            <span className="transfer-portal-stat-value">
                              {Math.round(player.xf_pct * 100)}%
                            </span>
                            <span className="transfer-portal-stat-label">
                              Percentile
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="transfer-portal-card-footer">
                        <div className="transfer-portal-eligibility">
                          <span className="transfer-portal-eligibility-label">
                            Years Remaining:
                          </span>
                          <span>{player.eligibility}</span>
                        </div>
                        <button
                          className="transfer-portal-view-details"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPlayerId(player.id);
                          }}
                        >
                          <AssessmentIcon fontSize="small" />
                          <span>Advanced Metrics</span>
                        </button>
                      </div>

                      <div className="transfer-portal-card-expanded">
                        <div className="transfer-portal-player-details">
                          <div className="transfer-portal-detail-item">
                            <span className="transfer-portal-detail-label">
                              <HeightIcon
                                fontSize="small"
                                style={{ marginRight: "4px" }}
                              />
                              Height:
                            </span>
                            <span className="transfer-portal-detail-value">
                              {player.height}
                            </span>
                          </div>
                          <div className="transfer-portal-detail-item">
                            <span className="transfer-portal-detail-label">
                              <FitnessCenterIcon
                                fontSize="small"
                                style={{ marginRight: "4px" }}
                              />
                              Weight:
                            </span>
                            <span className="transfer-portal-detail-value">
                              {player.weight ? `${player.weight} lbs` : "N/A"}
                            </span>
                          </div>
                          <div className="transfer-portal-detail-item">
                            <span className="transfer-portal-detail-label">
                              <EmailIcon
                                fontSize="small"
                                style={{ marginRight: "4px" }}
                              />
                              Personal Contact:
                            </span>
                            <span className="transfer-portal-detail-value">
                              {player.personal_contact || "N/A"}
                            </span>
                          </div>
                          <div className="transfer-portal-detail-item">
                            <span className="transfer-portal-detail-label">
                              <ContactPhoneIcon
                                fontSize="small"
                                style={{ marginRight: "4px" }}
                              />
                              Other Contact:
                            </span>
                            <span className="transfer-portal-detail-value">
                              {player.other_contact || "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {pagination.page < pagination.pages && (
                <div className="transfer-portal-load-more">
                  <button
                    className="transfer-portal-load-more-btn"
                    onClick={loadMorePlayers}
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <span>Loading...</span>
                    ) : (
                      <>
                        <AddIcon />
                        <span>See More Players</span>
                      </>
                    )}
                  </button>
                  {showFilterTip && (
                    <div className="transfer-portal-filter-tip">
                      <Tooltip title="You can use the filters at the top to narrow down your search">
                        <InfoIcon className="transfer-portal-info-icon" />
                      </Tooltip>
                      <span>
                        Pro Tip: Use the filters above to narrow your search!
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Render the modal only when there's a selected player */}
        {selectedPlayerId && (
          <PortalPlayerModal
            playerId={selectedPlayerId}
            onClose={handleCloseModal}
          />
        )}
      </div>
    </PageContainer>
  );
};

export default Portal;
